<script setup lang="ts">
import { ref, watch } from 'vue'
import { Button } from '@/components/ui/button'
import { Plus, Trash2, RefreshCw, Fish } from 'lucide-vue-next'
import BaseVisualEditor from './BaseVisualEditor.vue'
import MermaidCodeEditor from './MermaidCodeEditor.vue'
import MermaidLivePreview from './MermaidLivePreview.vue'

const props = defineProps<{
  open: boolean
}>()

const emit = defineEmits<{
  'update:open': [value: boolean]
  'save': [code: string]
}>()

const showCode = ref(true)
const copied = ref(false)
const editableCode = ref('')

interface IshikawaCategory {
  id: string
  name: string
  causesText: string
}

const problem = ref('Caída del Sistema')
const categories = ref<IshikawaCategory[]>([
  { id: crypto.randomUUID(), name: 'Infraestructura', causesText: 'Servidor caído, Base de datos saturada' },
  { id: crypto.randomUUID(), name: 'Personal', causesText: 'Falta de capacitación, Error humano' },
  { id: crypto.randomUUID(), name: 'Procesos', causesText: 'Despliegue sin pruebas, Ausencia de monitoreo' }
])

const generateId = () => crypto.randomUUID()

const addCategory = () => {
  categories.value.push({
    id: generateId(),
    name: `Nueva Categoría`,
    causesText: 'Causa 1, Causa 2'
  })
}

const removeCategory = (idx: number) => {
  categories.value.splice(idx, 1)
}

const generateMermaid = () => {
  let code = `ishikawa-beta\n`
  code += `  ${problem.value.trim() || 'Problema Principal'}\n`
  
  categories.value.forEach(cat => {
    if (cat.name.trim()) {
      code += `    ${cat.name.trim()}\n`
      const causes = cat.causesText.split(',').map(c => c.trim()).filter(c => c)
      causes.forEach(cause => {
        code += `      ${cause}\n`
      })
    }
  })
  
  editableCode.value = code
}

const syncToVisual = () => {
  const lines = editableCode.value.split('\n')
  let currentProblem = ''
  const newCategories: IshikawaCategory[] = []
  
  let currentCat: IshikawaCategory | null = null
  
  for (const line of lines) {
    if (line.trim() === 'ishikawa-beta' || !line.trim()) continue
    
    // Analizar la identación para deducir la jerarquía
    const indentMatch = line.match(/^(\s+)/)
    const indentCount = indentMatch ? indentMatch[1].length : 0
    const text = line.trim()
    
    if (indentCount === 2) {
       // Nivel de Problema
       currentProblem = text
    } else if (indentCount === 4) {
       // Nivel de Categoría
       currentCat = { id: generateId(), name: text, causesText: '' }
       newCategories.push(currentCat)
    } else if (indentCount >= 6) {
       // Nivel de Causa (lo agrupamos por comas si hay varios)
       if (currentCat) {
         if (currentCat.causesText) {
            currentCat.causesText += `, ${text}`
         } else {
            currentCat.causesText = text
         }
       }
    }
  }
  
  if (currentProblem) problem.value = currentProblem
  if (newCategories.length > 0) categories.value = newCategories
}

watch(() => props.open, (newVal) => {
  if (newVal) {
    const editingCode = localStorage.getItem('editingDiagramCode');
    if (editingCode) {
      setTimeout(() => {
        editableCode.value = editingCode;
        localStorage.removeItem('editingDiagramCode');
        syncToVisual();
      }, 300);
      return;
    }
  }

  if (newVal && !editableCode.value) {
    generateMermaid()
  }
}, { immediate: true })

watch([problem, categories], () => {
  generateMermaid()
}, { deep: true })

const copyToClipboard = async () => {
  try {
    await navigator.clipboard.writeText(editableCode.value)
    copied.value = true
    setTimeout(() => copied.value = false, 2000)
  } catch (err) {
    console.error('Failed to copy text: ', err)
  }
}

const saveAndClose = () => {
  emit('save', editableCode.value)
  emit('update:open', false)
}
</script>

<template>
  <BaseVisualEditor
    :open="open"
    @update:open="$emit('update:open', $event)"
    title="Diagrama Ishikawa (Espina de Pescado)"
    description="Identifica las causas raíz de un problema o efecto categorizándolas en ramas principales."
    :showCode="showCode"
    @update:showCode="showCode = $event"
    :copied="copied"
    @copy="copyToClipboard"
    @save="saveAndClose"
  >
    <template #icon>
      <Fish class="w-5 h-5 text-primary" />
    </template>

    <template #toolbar>
      <Button size="sm" variant="default" @click="addCategory"><Plus class="w-4 h-4 mr-2" /> Añadir Categoría</Button>
    </template>

    <template #canvas>
      <div class="h-full w-full overflow-y-auto p-6 bg-slate-50 dark:bg-slate-900/50 flex flex-col gap-6">
        
        <!-- Problema Principal -->
        <div class="bg-card border-2 border-red-200 dark:border-red-900/50 rounded-lg p-4 shadow-sm flex flex-col gap-2 relative overflow-hidden">
          <div class="absolute right-[-20px] top-[-20px] opacity-10 pointer-events-none">
            <Fish class="w-32 h-32 text-red-500" />
          </div>
          <h3 class="font-semibold text-lg text-red-600 dark:text-red-400">Problema / Efecto Central</h3>
          <input 
            v-model="problem" 
            class="bg-background border rounded px-3 py-2 text-base font-bold focus:outline-none focus:ring-2 focus:ring-red-500 w-full md:w-2/3"
            placeholder="Ej. Defecto de Producción"
          />
        </div>

        <!-- Categorías y Causas -->
        <div class="bg-card border rounded-lg p-4 shadow-sm flex flex-col gap-4">
          <h3 class="font-semibold text-lg border-b pb-2 flex items-center gap-2">
            Estructura de Causas (Espinas)
          </h3>
          
          <div v-if="categories.length === 0" class="text-center text-muted-foreground py-4 text-xs border border-dashed rounded">
            No hay categorías. El pescado solo tiene cabeza.
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div 
              v-for="(cat, idx) in categories" 
              :key="cat.id"
              class="flex flex-col gap-3 bg-muted/30 border p-3 rounded-md relative group hover:bg-muted/50 transition-colors"
            >
              <button 
                @click="removeCategory(idx)" 
                class="absolute right-2 top-2 text-muted-foreground hover:bg-destructive hover:text-white transition-colors p-1 rounded opacity-0 group-hover:opacity-100"
                title="Eliminar Categoría"
              >
                <Trash2 class="w-4 h-4" />
              </button>

              <div class="flex flex-col gap-1 pr-6">
                <label class="text-[10px] font-bold uppercase text-muted-foreground ml-1 text-primary">Categoría (Espina Mayor)</label>
                <input 
                  v-model="cat.name" 
                  class="bg-background border rounded px-2 py-1 text-sm font-semibold focus:ring-1 focus:outline-none focus:ring-primary" 
                  placeholder="Ej. Equipamiento" 
                />
              </div>

              <div class="flex flex-col gap-1">
                <label class="text-[10px] font-bold uppercase text-muted-foreground ml-1">Causas (Separadas por coma)</label>
                <textarea 
                  v-model="cat.causesText" 
                  class="bg-background border rounded px-2 py-2 text-sm focus:ring-1 focus:outline-none resize-none min-h-[60px]" 
                  placeholder="Ej. Máquina obsoleta, Falta de mantenimiento" 
                ></textarea>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #code-panel>
      <div class="text-sm font-bold p-2 bg-muted-foreground/10 text-muted-foreground flex items-center justify-between border-b shrink-0">
        Mermaid (Sincronizado)
        <Button size="sm" variant="default" class="h-7 text-xs" @click="syncToVisual">
          <RefreshCw class="w-3 h-3 mr-2" />
          Sincronizar a Visual
        </Button>
      </div>
      <MermaidCodeEditor v-model="editableCode" class="flex-1 min-h-0" />
    </template>

    <template #live-preview>
      <MermaidLivePreview :code="editableCode" />
    </template>
  </BaseVisualEditor>
</template>
